import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/category', label: '剧集分类' },
  { to: '/about', label: '关于 / 帮助' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/category?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50 border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 dark:text-foreground-950">
              <i className="ri-clapperboard-fill text-xl"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-base md:text-lg font-bold text-foreground-950 whitespace-nowrap">
                迷人的保姆在线观看
              </span>
              <span className="hidden sm:block text-[11px] text-foreground-500 whitespace-nowrap">
                高清电视剧在线播放平台
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive(item.to)
                    ? 'text-primary-700 bg-primary-100 font-medium'
                    : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索剧集 / 章节关键词"
                className="w-56 xl:w-72 h-10 pl-9 pr-3 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition"
              />
            </form>
            <Link
              to="/category?filter=favorites"
              className="relative w-10 h-10 rounded-full bg-background-100 border border-background-200 flex items-center justify-center text-foreground-700 hover:text-accent-600 hover:border-accent-300 transition-colors"
              title="我的收藏"
            >
              <i className="ri-heart-3-line text-lg"></i>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-[10px] font-label flex items-center justify-center">
                  {favorites.length}
                </span>
              )}
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Link
              to="/category?filter=favorites"
              className="relative w-10 h-10 rounded-full bg-background-100 border border-background-200 flex items-center justify-center text-foreground-700"
              title="我的收藏"
            >
              <i className="ri-heart-3-line text-lg"></i>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-[10px] font-label flex items-center justify-center">
                  {favorites.length}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 rounded-lg bg-background-100 border border-background-200 flex items-center justify-center text-foreground-800"
              aria-label="打开菜单"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden pb-5 pt-1 animate-fade-in">
            <form onSubmit={handleSearch} className="relative mb-3">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索剧集 / 章节关键词"
                className="w-full h-11 pl-9 pr-3 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </form>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-4 py-3 text-sm rounded-md transition-colors ${
                    isActive(item.to)
                      ? 'text-primary-700 bg-primary-100 font-medium'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}